// =======================================================================================================
import { ReactNode, Component, ReactElement } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import type { LocalizationMap } from '@Primitive/Localization/Localization';
import { DebugEx } from '@Utils/DebugEx';
import { DomUtils } from '@Utils/Dom';
// =======================================================================================================
export enum CameraResolution {
    Max1080P,
    Max720P,
    Max480P,
    Max240P
}
export enum CameraMode {
    Stoped,
    Started,
    Preview
}
export interface CameraData {
    CameraResolution: CameraResolution;
    IconFontSize: number;
    TextFontSize: number;
    BoldText: boolean;
}
// ------------------------------------------------------------------------------------------------------
export interface CameraState {
    Mode: CameraMode;
}
// ------------------------------------------------------------------------------------------------------
class Camera extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<CameraData>, CameraState> {
    labelFor = "";
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<CameraData>) {
        super(props);
        this.state = {
            Mode: CameraMode.Stoped
        };
        this.labelFor = getRandomSmallKey();
    }
    // ---------------------------------------------------------------------------------------------------
    render() {

        let label = this.props.LocalizationMap[takeAPictureKey];
        if (!isDefined(label) || IsNullOrEmpty(label))
            label = takeAPictureValue;

        let widgetData = this.props.WidgetData;
        return <div style={{
            width: this.props.width,
            height: this.props.height,
            overflow: 'hidden',
        }}>
            <label
                key="label"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: this.props.height,
                    width: "100%",
                    cursor: "pointer",
                    fontSize: widgetData.TextFontSize,
                    fontWeight: widgetData.BoldText ? 'bold' : 'normal'
                }}
                htmlFor={this.labelFor}
            >
                <div key="label"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <i className="fas fa-camera" style={{
                        fontSize: widgetData.IconFontSize,
                        padding: 10
                    }}></i>
                    {label}
                </div>
            </label>
            <input id={this.labelFor} key="input" type="file" accept="image/*" capture style={{ display: 'none' }}
                onChange={(e) => {
                    if (e.target) {
                        var files = e.target.files;
                        if (files && files.length > 0) {
                            var FR = new FileReader();
                            FR.onload = (eFR: any) => {
                                let value = eFR.target.result;

                                // if value is empty/null do nothing
                                if (IsNullOrEmpty(value)) {
                                    DebugEx.TraceError("Loaded empty photo");
                                    return;
                                }

                                let wRes = 1280;
                                let hRes = 720;
                                switch (this.props.WidgetData.CameraResolution) {
                                    case CameraResolution.Max1080P:
                                        wRes = 1920;
                                        hRes = 1080;
                                        break;
                                    case CameraResolution.Max720P:
                                        wRes = 1280;
                                        hRes = 720;
                                        break;
                                    case CameraResolution.Max480P:
                                        wRes = 640;
                                        hRes = 480;
                                        break;
                                    case CameraResolution.Max240P:
                                        wRes = 320;
                                        hRes = 240;
                                        break;
                                }

                                // TODO: have more dynamic way to resize it and the output quality
                                DomUtils.getResizedImage(value, wRes, hRes, "jpeg", 0.8)
                                    .then((res) => this.props.SendPortEvent("img", res.src))
                                    .catch((er) => DebugEx.TraceError("Failed to resize the image."));

                            };
                            FR.onerror = (eFR: ProgressEvent) => { DebugEx.TraceError("Failed to load the photo.") };
                            FR.readAsDataURL(files.item(0));
                        }
                    }
                }}>
            </input>
        </div>;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// Settings
// ---------------------------------------------------------------------------------------------------
interface CameraWidgetSettingsState {
    WidgetData: CameraData;
    UnSavedSettings: boolean;
}
// ---------------------------------------------------------------------------------------------------
class CameraWidgetSettings extends Component<IWidgetSettingsProps<CameraData>, CameraWidgetSettingsState> {
    constructor(props: IWidgetSettingsProps<CameraData>) {
        super(props);

        this.state = {
            WidgetData: _.cloneDeep(this.props.WidgetData),
            UnSavedSettings: false
        };
    }
    // ---------------------------------------------------------------------------------------------------
    saveData() {
        if (isDefined(this.props.onWidgetDataUpdate))
            this.props.onWidgetDataUpdate(this.state.WidgetData);

        this.setState({ UnSavedSettings: false });
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<CameraData>[] = [
            {
                name: "",
                elements: [{
                    type: SettingsType.DropDown,
                    tooltip: 'The resolution of the image to be imported. Available options: 1080p, 720p, 480p, 240p.',
                    dataPath: "CameraResolution",
                    label: "Max Resolution",
                    items: [
                        { label: "1080p", value: CameraResolution.Max1080P },
                        { label: "720p", value: CameraResolution.Max720P },
                        { label: "480p", value: CameraResolution.Max480P },
                        { label: "240p", value: CameraResolution.Max240P },
                    ]
                },
                {
                    type: SettingsType.Number,
                    label: 'Icon Font Size',
                    dataPath: 'IconFontSize',
                    tooltip: 'Sets the font-size of icon to a fixed size in px',
                    oneLine: true,
                    minValue: 8,
                    maxValue: 32
                },
                {
                    type: SettingsType.Number,
                    label: 'Text Font Size',
                    dataPath: 'TextFontSize',
                    tooltip: 'Sets the font-size of text to a fixed size in px',
                    oneLine: true,
                    minValue: 8,
                    maxValue: 32
                },
                {
                    type: SettingsType.Boolean,
                    label: 'Bold Text',
                    dataPath: 'BoldText',
                    tooltip: 'Whether to have thick characters for text or not'
                }]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// =======================================================================================================
// Description interface
export class CameraWidget extends IWidgetThingsClass<CameraData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return <Camera />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <CameraWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];

        // Image
        ports.push(new WebThingPort(
            {
                Name: "Image",
                PortKey: "img",
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            },
            WebThingPortType.Data
        ));

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): CameraData {
        return {
            CameraResolution: CameraResolution.Max720P,
            IconFontSize: 30,
            TextFontSize: 25,
            BoldText: true
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return CameraWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: CameraData = CameraWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Camera",
            WidgetTitle: "",
            Key: "CameraWidget",
            Type: "CameraWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/camera.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        // todo: consider moving keys to 'primitive' localization group
        const res = { [takeAPictureKey]: takeAPictureValue };

        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    };
    // ------------------------------------------------------------------------------------------------
}
const takeAPictureKey = 'take-a-pic';
const takeAPictureValue = 'Take a picture';
// =======================================================================================================